import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetActiveSystemNotices, useAcknowledgeSystemNotice, getGetActiveSystemNoticesQueryKey } from '@/api/auth/system-notices/system-notices';
import type { ActiveNoticeResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { useOrgs } from './OrgsContext';
import { useLanguage } from './LanguageContext';
import { useRoles } from './RolesContext';
import { toast } from 'sonner';
import { useError } from '@/contexts/ErrorContext';

/**
 * System notices context type definition
 */
interface SystemNoticesContextType {
  /**
   * All active notices
   */
  notices: ActiveNoticeResponse[];
  /**
   * Informational notices (severity: info)
   */
  infoNotices: ActiveNoticeResponse[];
  /**
   * Urgent notices (severity: urgent)
   */
  urgentNotices: ActiveNoticeResponse[];
  /**
   * Whether notices are currently loading
   */
  isLoading: boolean;
  /**
   * Error if notices failed to load
   */
  error: Error | null;
  /**
   * Acknowledge a notice
   */
  acknowledgeNotice: (params: { noticeId: string; isOrgLevel?: boolean }) => Promise<void>;
  /**
   * Check if user can acknowledge a notice at org level
   */
  canAcknowledgeForOrg: boolean;
}

const SystemNoticesContext = createContext<SystemNoticesContextType | undefined>(undefined);

const PRODUCT_ID = 'data-mission';

/**
 * Provider component that manages system notices state
 */
export function SystemNoticesProvider({ children }: { children: ReactNode }) {
  const { selectedOrganizationId } = useOrgs();
  const { language, t } = useLanguage();
  const { isAdmin } = useRoles();
  const { handleApiError } = useError();
  const queryClient = useQueryClient();

  // Fetch active notices
  const { data: noticesResponse, isLoading, error } = useGetActiveSystemNotices(
    {
      productId: PRODUCT_ID,
      language,
      orgId: selectedOrganizationId ?? '',
      includeAcknowledged: false,
    },
    {
      query: {
        enabled: !!selectedOrganizationId,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        retry: 1,
      },
    }
  );

  const notices = noticesResponse?.data ?? [];

  console.log('[SystemNotices] Current state:', {
    totalNotices: notices.length,
    infoCount: notices.filter((n) => n.severity === 'info').length,
    urgentCount: notices.filter((n) => n.severity === 'urgent').length,
    isLoading,
    hasError: !!error,
    allSeverities: notices.map((n) => ({ id: n.id, severity: n.severity, title: n.title })),
  });

  // Filter notices by severity (case-insensitive)
  const infoNotices = notices.filter((notice) => notice.severity.toLowerCase() === 'info');
  const urgentNotices = notices.filter((notice) => notice.severity.toLowerCase() === 'urgent');

  // Acknowledge notice mutation
  const { mutateAsync: acknowledgeMutation } = useAcknowledgeSystemNotice({
    mutation: {
      onSuccess: () => {
        // Invalidate notices query to refetch
        void queryClient.invalidateQueries({
          queryKey: getGetActiveSystemNoticesQueryKey({
            productId: PRODUCT_ID,
            language,
            orgId: selectedOrganizationId ?? '',
            includeAcknowledged: false,
          }),
        });
        toast.success(t.systemNotices.acknowledgedSuccessfully);
      },
      onError: (error) => {
        handleApiError(error, { context: 'SystemNoticesContext.acknowledgeNotice' });
      },
    },
  });

  /**
   * Acknowledge a notice
   */
  const acknowledgeNotice = useCallback(
    async ({ noticeId, isOrgLevel = false }: { noticeId: string; isOrgLevel?: boolean }) => {
      console.log('[SystemNotices] Acknowledging notice:', {
        noticeId,
        isOrgLevel,
        orgId: selectedOrganizationId,
        isAdmin,
      });

      if (!selectedOrganizationId) {
        console.error('[SystemNotices] Cannot acknowledge - no organization selected');
        // eslint-disable-next-line no-restricted-syntax
        throw new Error('No organization selected');
      }

      // For org-level acknowledgements, verify user is admin
      if (isOrgLevel && !isAdmin) {
        console.warn('[SystemNotices] User lacks permission for org-level acknowledgement');
        toast.error(t.systemNotices.noPermissionToAcknowledge);
        return;
      }

      await acknowledgeMutation({
        noticeId,
        data: {
          orgId: selectedOrganizationId,
        },
      });

      console.log('[SystemNotices] Notice acknowledged successfully:', noticeId);
    },
    [selectedOrganizationId, isAdmin, acknowledgeMutation, t]
  );

  return (
    <SystemNoticesContext.Provider
      value={{
        notices,
        infoNotices,
        urgentNotices,
        isLoading,
        error: error as Error | null,
        acknowledgeNotice,
        canAcknowledgeForOrg: isAdmin,
      }}
    >
      {children}
    </SystemNoticesContext.Provider>
  );
}

/**
 * Hook to access system notices context
 */
export function useSystemNotices() {
  const context = useContext(SystemNoticesContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useSystemNotices must be used within a SystemNoticesProvider');
  }
  return context;
}
